import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useVisitorLookup } from '../hooks/useVisitorLookup';
import api from '../services/api';

const GLOBAL_ROLES    = ['superadmin', 'security_head', 'admin_head'];
const PURPOSE_OPTIONS = ['Meeting', 'Interview', 'Vendor Visit', 'Personal', 'Other'];

const INITIAL_FORM = {
  visitorName: '', visitorPhone: '', visitorAddress: '', visitorCompany: '',
  toWhomMeet: '', purposeType: 'Meeting', purposeOther: '', vendorCompany: '',
  department: '', remarks: '', totalVisitors: 1,
};
const INITIAL_ERRORS = {
  visitorPhone: '', visitorName: '', toWhomMeet: '', officeId: '',
  visitorAddress: '', purposeOther: '', vendorCompany: '', visitorCompany: '',
};

/* ─── Shared styles ─────────────────────────────────────────────── */
const INP = {
  height: 54, fontSize: '1.05rem', borderRadius: 12,
  border: '1.5px solid #dee2e6', padding: '0 16px',
  background: '#fff', width: '100%', outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s', display: 'block',
  fontFamily: 'inherit',
};
const LBL = {
  fontSize: '0.95rem', fontWeight: 600, color: '#374151',
  marginBottom: 6, display: 'block',
};
const ERR = { color: '#ef4444', fontSize: '0.82rem', marginTop: 4 };

/* ─── Section card header ───────────────────────────────────────── */
const SectionHeader = ({ icon, label, color, bg }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
    <div style={{ width: 34, height: 34, borderRadius: 9, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <i className={`bi ${icon}`} style={{ color, fontSize: '1rem' }}></i>
    </div>
    <span style={{ fontSize: '0.85rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════════ */
const VisitorRegistration = () => {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const { visitor: foundVisitor, loading: lookupLoading, lookup, clearVisitor } = useVisitorLookup();

  const isGlobalRole    = GLOBAL_ROLES.includes(user?.role);
  const isReceptionMode = user?.role === 'guard';

  /* ── Mode ── */
  const [mode, setMode] = useState('register');

  /* ── Register state ── */
  const [form, setForm]             = useState(INITIAL_FORM);
  const [errors, setErrors]         = useState(INITIAL_ERRORS);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]       = useState(null);
  const [countdown, setCountdown]   = useState(0);
  const [selectedOfficeId, setSelectedOfficeId] = useState('');
  const [offices, setOffices]       = useState([]);
  const [activeVisitId, setActiveVisitId] = useState(null);

  /* ── Employee search ── */
  const [empSuggestions,  setEmpSuggestions]  = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [empSearching,    setEmpSearching]    = useState(false);
  const [hostEmail,       setHostEmail]       = useState('');
  const empTimerRef   = useRef(null);
  const empFocusedRef = useRef(false);

  /* ── Checkout state ── */
  const [coSearchType,  setCoSearchType]  = useState('phone'); // 'phone' | 'name'
  const [coPhone,       setCoPhone]       = useState('');
  const [coName,        setCoName]        = useState('');
  const [coSearched,    setCoSearched]    = useState(false);
  const [coLoading,     setCoLoading]     = useState(false);
  const [coVisit,       setCoVisit]       = useState(null);   // single confirmed visit
  const [coVisits,      setCoVisits]      = useState([]);     // name search results list
  const [coDone,        setCoDone]        = useState(false);
  const [coCheckingOut, setCoCheckingOut] = useState(false);
  const [coCountdown,   setCoCountdown]   = useState(5);

  /* ── Load offices ── */
  useEffect(() => {
    if (!isGlobalRole) return;
    api.get('/offices').then(({ data }) => setOffices(data.offices || [])).catch(() => {});
  }, [isGlobalRole]);

  /* ── Reception auto-reset after registration ── */
  useEffect(() => {
    if (!isReceptionMode || !success) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCountdown(4);
    const iv  = setInterval(() => setCountdown(c => (c <= 1 ? (clearInterval(iv), 0) : c - 1)), 1000);
    const tim = setTimeout(() => handleReset(), 4000);
    return () => { clearInterval(iv); clearTimeout(tim); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, isReceptionMode]);

  /* ── Auto-return to Register tab after checkout ── */
  useEffect(() => {
    if (!coDone) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCoCountdown(5);
    const iv  = setInterval(() => setCoCountdown(c => (c <= 1 ? (clearInterval(iv), 0) : c - 1)), 1000);
    const tim = setTimeout(() => { resetCheckout(); setMode('register'); }, 5000);
    return () => { clearInterval(iv); clearTimeout(tim); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coDone]);

  /* ── Scroll to top on mode/tab change ── */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [mode]);

  /* ── Auto-fill returning visitor ── */
  useEffect(() => {
    if (!foundVisitor) return;
    setForm(f => ({
      ...f,
      visitorName:    foundVisitor.name    || '',
      visitorAddress: foundVisitor.address || '',
      visitorCompany: foundVisitor.company || '',
    }));
    setErrors(e => ({ ...e, visitorName: '', visitorAddress: '', visitorCompany: '' }));
    toast.info(`↩ Returning visitor: ${foundVisitor.name} — details auto-filled`);
  }, [foundVisitor]);

  /* ── Employee search ── */
  const searchEmployees = useCallback((val) => {
    if (empTimerRef.current) clearTimeout(empTimerRef.current);
    if (!val || val.length < 2) { setEmpSuggestions([]); setShowSuggestions(false); setEmpSearching(false); return; }
    setEmpSearching(true); setShowSuggestions(true);
    empTimerRef.current = setTimeout(async () => {
      try {
        const officeParam = isGlobalRole && selectedOfficeId ? `&officeId=${selectedOfficeId}` : '';
        const { data } = await api.get(`/graph/employees?q=${encodeURIComponent(val)}${officeParam}`);
        const filtered = (data.employees || []).filter(e => e.name?.toLowerCase().includes(val.toLowerCase()));
        setEmpSuggestions(filtered);
        if (empFocusedRef.current) setShowSuggestions(true);
      } catch { setEmpSuggestions([]); setShowSuggestions(false); }
      finally  { setEmpSearching(false); }
    }, 350);
  }, [isGlobalRole, selectedOfficeId]);

  const selectEmployee = (emp) => {
    setForm(f => ({ ...f, toWhomMeet: emp.name, department: emp.department || f.department }));
    setHostEmail(emp.email || '');
    setErrors(er => ({ ...er, toWhomMeet: '' }));
    setEmpSuggestions([]); setShowSuggestions(false);
  };

  /* ── Field change ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    const san = name === 'visitorPhone' ? value.replace(/[^0-9]/g, '')
              : name === 'visitorName'  ? value.replace(/[^a-zA-Z\s]/g, '')
              : value;
    setForm(f => ({ ...f, [name]: san }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
    if (name === 'visitorPhone') { lookup(san); setActiveVisitId(null); }
    if (name === 'toWhomMeet')   searchEmployees(san);
  };

  /* ── Validators ── */
  const vPhone = (v) => {
    if (!v.trim())              return 'Mobile number is required.';
    if (!/^[0-9]{10}$/.test(v.trim())) return 'Must be exactly 10 digits.';
    if (/^(\d)\1{9}$/.test(v.trim())) return 'Invalid mobile number.';
    return '';
  };
  const vAddr = (v) => {
    const t = v.trim();
    if (!t)           return 'Address is required.';
    if (t.length < 5) return 'Address is too short.';
    if (!/[a-zA-Z]/.test(t)) return 'Address must contain letters.';
    return '';
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'visitorPhone')   setErrors(er => ({ ...er, visitorPhone:   vPhone(value) }));
    if (name === 'visitorAddress') setErrors(er => ({ ...er, visitorAddress: vAddr(value) }));
    if (name === 'visitorName') {
      if (!value.trim())           setErrors(er => ({ ...er, visitorName: 'Visitor name is required.' }));
      else if (value.trim().length < 2) setErrors(er => ({ ...er, visitorName: 'At least 2 characters.' }));
      else                         setErrors(er => ({ ...er, visitorName: '' }));
    }
    if (name === 'visitorCompany') {
      setErrors(er => ({ ...er, visitorCompany: value.trim() ? '' : 'Company name is required.' }));
    }
    if (name === 'toWhomMeet') {
      setErrors(er => ({ ...er, toWhomMeet: value.trim() ? '' : 'Please enter who the visitor is meeting.' }));
    }
  };

  const upsertToast = (id, msg) => {
    if (toast.isActive(id)) toast.update(id, { render: msg, autoClose: 4000 });
    else                    toast.error(msg, { toastId: id, autoClose: 4000 });
  };

  const validate = () => {
    const e = { ...INITIAL_ERRORS };
    e.visitorPhone   = vPhone(form.visitorPhone);
    e.visitorAddress = vAddr(form.visitorAddress);
    if (!form.visitorName.trim())                e.visitorName    = 'Visitor name is required.';
    else if (form.visitorName.trim().length < 2) e.visitorName    = 'At least 2 characters.';
    if (!form.visitorCompany.trim())             e.visitorCompany = 'Company name is required.';
    if (!form.toWhomMeet.trim())                 e.toWhomMeet     = 'Please enter who the visitor is meeting.';
    if (form.purposeType === 'Other' && !form.purposeOther.trim())       e.purposeOther  = 'Please specify the purpose.';
    if (form.purposeType === 'Vendor Visit' && !form.vendorCompany.trim()) e.vendorCompany = 'Vendor company name is required.';
    if (isGlobalRole && !selectedOfficeId)       e.officeId       = 'Please select an office.';
    setErrors(e);
    const hasErr = Object.values(e).some(Boolean);
    if (hasErr) {
      if (e.visitorPhone)   upsertToast('err-ph', e.visitorPhone);
      if (e.visitorName)    upsertToast('err-nm', e.visitorName);
      if (e.visitorCompany) upsertToast('err-co', e.visitorCompany);
      if (e.toWhomMeet)     upsertToast('err-mt', e.toWhomMeet);
      if (e.visitorAddress) upsertToast('err-ad', e.visitorAddress);
      if (e.officeId)       upsertToast('err-of', e.officeId);
    }
    return !hasErr;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true); setActiveVisitId(null);
    try {
      const officeId      = isGlobalRole ? selectedOfficeId : (user?.office?._id || user?.office);
      const derivedPurpose = form.purposeType === 'Personal' ? 'Personal' : 'Official';
      const derivedDetails = form.purposeType === 'Other'        ? form.purposeOther
                           : form.purposeType === 'Vendor Visit' ? `Vendor Visit — ${form.vendorCompany}`
                           : form.purposeType;
      const { purposeType, purposeOther, vendorCompany, ...rest } = form;
      const { data } = await api.post('/visits', {
        ...rest, purpose: derivedPurpose, purposeDetails: derivedDetails,
        visitorId: foundVisitor?._id || undefined, officeId,
        meetingPersonEmail: hostEmail || undefined,
        totalVisitors: parseInt(form.totalVisitors) || 1,
      });
      setSuccess(data.visit);
      toast.success('✅ Visitor registered successfully!');
    } catch (err) {
      const msg  = err.response?.data?.message || 'Registration failed.';
      const avId = err.response?.data?.activeVisitId;
      if (err.response?.status === 409 && avId) {
        setActiveVisitId(avId);
        toast.error(`⚠️ ${msg}`, { autoClose: 8000 });
      } else {
        toast.error(`❌ ${msg}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm(INITIAL_FORM); setErrors(INITIAL_ERRORS);
    setSuccess(null); setHostEmail(''); setEmpSuggestions([]);
    setActiveVisitId(null);
    clearVisitor();
    if (isGlobalRole) setSelectedOfficeId('');
  };

  /* ── Checkout helpers ── */
  const searchMyVisit = async () => {
    if (coSearchType === 'phone') {
      if (!coPhone || coPhone.length < 10) { toast.error('Enter a valid 10-digit mobile number.'); return; }
      setCoLoading(true); setCoSearched(true); setCoVisit(null); setCoVisits([]);
      try {
        const { data } = await api.get(`/visits/active?q=${coPhone}`);
        const match = (data.visits || []).find(v => v.visitor?.phone === coPhone);
        setCoVisit(match || null);
      } catch { setCoVisit(null); }
      finally { setCoLoading(false); }
    } else {
      if (!coName || coName.trim().length < 2) { toast.error('Enter at least 2 characters.'); return; }
      setCoLoading(true); setCoSearched(true); setCoVisit(null); setCoVisits([]);
      try {
        const { data } = await api.get(`/visits/active?q=${encodeURIComponent(coName.trim())}`);
        const matches = (data.visits || []).filter(v =>
          v.visitor?.name?.toLowerCase().includes(coName.trim().toLowerCase())
        );
        setCoVisits(matches);
        if (matches.length === 1) setCoVisit(matches[0]);
      } catch { setCoVisits([]); }
      finally { setCoLoading(false); }
    }
  };

  const confirmCheckout = async () => {
    if (!coVisit) return;
    setCoCheckingOut(true);
    try {
      await api.patch(`/visits/${coVisit._id}/checkout`);
      setCoDone(true);
      toast.success('✅ Checkout successful! Have a great day!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Checkout failed. Please ask reception for help.');
    } finally { setCoCheckingOut(false); }
  };

  const resetCheckout = () => {
    setCoPhone(''); setCoName(''); setCoSearched(false);
    setCoVisit(null); setCoVisits([]); setCoDone(false);
  };

  /* ══════════════════════════════════════════════════════════════
     Reception success screen
  ══════════════════════════════════════════════════════════════ */
  if (success && isReceptionMode) {
    return (
      <div style={{ minHeight: 'calc(100vh - 64px)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#e8f5ee,#f0fff5)', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 520, width: '100%' }}>
          <div style={{ width: 100, height: 100, borderRadius: '50%', background: '#086F3C', boxShadow: '0 8px 32px rgba(8,111,60,0.35)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
            <i className="bi bi-check-lg text-white" style={{ fontSize: '3.2rem' }}></i>
          </div>
          <h2 style={{ fontWeight: 900, color: '#086F3C', marginBottom: 4, fontSize: '2rem' }}>Entry Recorded!</h2>
          <p style={{ color: '#6c757d', marginBottom: 24, fontSize: '1.05rem' }}>Visitor pass issued successfully.</p>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, marginBottom: 24, textAlign: 'left', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            {[
              { icon: 'bi-hash',           label: 'Visitor ID', value: <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{success.serialNumber}</span> },
              { icon: 'bi-person-fill',    label: 'Visitor',    value: success.visitor?.name },
              { icon: 'bi-phone-fill',     label: 'Mobile',     value: success.visitor?.phone },
              { icon: 'bi-people-fill',    label: 'Group Size', value: success.totalVisitors > 1 ? `${success.totalVisitors} people` : '1 person' },
              { icon: 'bi-building',       label: 'Company',    value: success.visitor?.company },
              { icon: 'bi-person-check',   label: 'Meeting',    value: success.toWhomMeet },
              { icon: 'bi-briefcase-fill', label: 'Purpose',    value: (
                <span style={{ background: success.purpose === 'Personal' ? '#086F3C' : '#F2652C', color: '#fff', padding: '2px 10px', borderRadius: 20, fontSize: '0.88rem', fontWeight: 600 }}>
                  {success.purposeDetails || success.purpose}
                </span>
              )},
              { icon: 'bi-clock-fill', label: 'Time In', value: new Date(success.timeIn).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) },
            ].map(({ icon, label, value }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6c757d', fontSize: '0.95rem' }}>
                  <i className={`bi ${icon}`} style={{ color: '#086F3C' }}></i>{label}
                </span>
                <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{value}</span>
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#6c757d', fontSize: '0.92rem', marginBottom: 8 }}>
              <span className="spinner-border spinner-border-sm" style={{ color: '#086F3C', width: 14, height: 14, borderWidth: 2 }}></span>
              Next registration in <strong style={{ color: '#086F3C' }}>{countdown}s</strong>…
            </div>
            <div style={{ height: 6, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: '#086F3C', width: `${(countdown / 4) * 100}%`, transition: 'width 1s linear', borderRadius: 4 }} />
            </div>
          </div>
          <button onClick={handleReset} style={{ background: '#F2652C', color: '#fff', border: 'none', borderRadius: 12, padding: '14px 32px', fontSize: '1.05rem', fontWeight: 700, cursor: 'pointer', width: '100%' }}>
            <i className="bi bi-person-plus me-2"></i>Register Next Visitor
          </button>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════════
     Non-guard success screen
  ══════════════════════════════════════════════════════════════ */
  if (success) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-6 col-lg-5">
            <div style={{ background: '#fff', borderRadius: 20, padding: 40, textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
              <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#e8f5ee', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <i className="bi bi-check-circle-fill" style={{ fontSize: '2.5rem', color: '#086F3C' }}></i>
              </div>
              <h4 style={{ fontWeight: 800, marginBottom: 4 }}>Entry Recorded!</h4>
              <p style={{ color: '#6c757d', marginBottom: 24 }}>Visitor registered successfully.</p>
              <div style={{ background: '#f8f9fa', borderRadius: 12, padding: 16, textAlign: 'left', marginBottom: 20 }}>
                {[
                  { label: 'Visitor ID', value: <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{success.serialNumber}</span> },
                  { label: 'Visitor',    value: success.visitor?.name },
                  { label: 'Mobile',     value: success.visitor?.phone },
                  { label: 'Group Size', value: success.totalVisitors > 1 ? `${success.totalVisitors} people` : '1 person' },
                  { label: 'Company',    value: success.visitor?.company },
                  { label: 'Meeting',    value: success.toWhomMeet },
                  { label: 'Purpose',    value: success.purpose },
                  { label: 'Time In',    value: new Date(success.timeIn).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) },
                ].map(({ label, value }) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e9ecef', fontSize: '0.92rem' }}>
                    <span style={{ color: '#6c757d' }}>{label}</span><span style={{ fontWeight: 600 }}>{value}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button onClick={handleReset} style={{ background: '#F2652C', color: '#fff', border: 'none', borderRadius: 12, padding: '12px', fontSize: '1rem', fontWeight: 700, cursor: 'pointer' }}>
                  <i className="bi bi-person-plus me-2"></i>Register Another Visitor
                </button>
                <button onClick={() => navigate('/records')} style={{ background: '#fff', color: '#6c757d', border: '1.5px solid #dee2e6', borderRadius: 12, padding: '12px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer' }}>
                  <i className="bi bi-table me-2"></i>View All Records
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════════
     MAIN PAGE
  ══════════════════════════════════════════════════════════════ */
  return (
    <div style={{ background: '#f0f4f8', minHeight: 'calc(100vh - 64px)' }}>

      {/* ── Tab bar ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e9ecef', padding: '14px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, maxWidth: 1200, margin: '0 auto' }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 8, background: '#f0f4f8', padding: 4, borderRadius: 12 }}>
            {[
              { key: 'register', label: 'Register Visitor', icon: 'bi-person-plus-fill', activeColor: '#086F3C' },
              { key: 'checkout', label: 'Checkout',          icon: 'bi-box-arrow-right',  activeColor: '#F2652C' },
            ].map(t => (
              <button key={t.key} type="button" onClick={() => { if (t.key === 'checkout') handleReset(); else resetCheckout(); setMode(t.key); }}
                style={{
                  border: 'none', borderRadius: 9, padding: '10px 24px', fontSize: '0.98rem', fontWeight: 700, cursor: 'pointer',
                  background: mode === t.key ? t.activeColor : 'transparent',
                  color:      mode === t.key ? '#fff' : '#6c757d',
                  transition: 'all 0.15s',
                  boxShadow: mode === t.key ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                }}>
                <i className={`bi ${t.icon} me-2`}></i>{t.label}
              </button>
            ))}
          </div>
          {/* Date */}
          <div style={{ fontSize: '0.9rem', color: '#6c757d', fontWeight: 500 }}>
            <i className="bi bi-calendar3 me-2"></i>
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════
          CHECKOUT MODE — Privacy Safe
      ════════════════════════════════════════════════ */}
      {mode === 'checkout' && (
        <div style={{ minHeight: 'calc(100vh - 130px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ width: '100%', maxWidth: 520 }}>

            {/* ── Done state ── */}
            {coDone && (
              <div style={{ background: '#fff', borderRadius: 20, padding: 40, textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
                <div style={{ width: 90, height: 90, borderRadius: '50%', background: '#086F3C', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20, boxShadow: '0 6px 24px rgba(8,111,60,0.35)' }}>
                  <i className="bi bi-check-lg text-white" style={{ fontSize: '2.8rem' }}></i>
                </div>
                <h3 style={{ fontWeight: 800, color: '#086F3C', marginBottom: 8 }}>Checked Out!</h3>
                <p style={{ color: '#6c757d', fontSize: '1.05rem', marginBottom: 16 }}>Thank you for visiting Think Gas.<br/>Have a great day! 👋</p>
                <p style={{ color: '#adb5bd', fontSize: '0.88rem', marginBottom: 24 }}>
                  Returning to registration in <strong style={{ color: '#086F3C' }}>{coCountdown}s</strong>…
                </p>
                <button onClick={() => { resetCheckout(); setMode('register'); }}
                  style={{ background: '#086F3C', color: '#fff', border: 'none', borderRadius: 12, padding: '14px 32px', fontSize: '1.05rem', fontWeight: 700, cursor: 'pointer', width: '100%' }}>
                  <i className="bi bi-arrow-left me-2"></i>Back Now
                </button>
              </div>
            )}

            {/* ── Search state ── */}
            {!coDone && !coSearched && (
              <div style={{ background: '#fff', borderRadius: 20, padding: 36, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <div style={{ width: 70, height: 70, borderRadius: '50%', background: '#fff3ee', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                    <i className="bi bi-box-arrow-right" style={{ color: '#F2652C', fontSize: '2rem' }}></i>
                  </div>
                  <h4 style={{ fontWeight: 800, color: '#1a1a2e', marginBottom: 6, fontSize: '1.4rem' }}>Checkout</h4>
                  <p style={{ color: '#6c757d', fontSize: '1rem', margin: 0 }}>Search by mobile number or visitor name</p>
                </div>

                {/* Search type toggle */}
                <div style={{ display: 'flex', gap: 8, background: '#f0f4f8', padding: 4, borderRadius: 12, marginBottom: 20 }}>
                  {[
                    { key: 'phone', label: 'Mobile Number', icon: 'bi-phone-fill' },
                    { key: 'name',  label: 'Visitor Name',  icon: 'bi-person-fill' },
                  ].map(t => (
                    <button key={t.key} type="button"
                      onClick={() => { setCoSearchType(t.key); setCoPhone(''); setCoName(''); }}
                      style={{ flex: 1, border: 'none', borderRadius: 9, padding: '10px', fontSize: '0.92rem', fontWeight: 700, cursor: 'pointer',
                        background: coSearchType === t.key ? '#F2652C' : 'transparent',
                        color: coSearchType === t.key ? '#fff' : '#6c757d',
                        transition: 'all 0.15s',
                      }}>
                      <i className={`bi ${t.icon} me-2`}></i>{t.label}
                    </button>
                  ))}
                </div>

                {coSearchType === 'phone' ? (
                  <>
                    <label style={{ ...LBL, fontSize: '1rem' }}>Mobile Number</label>
                    <input
                      type="tel" inputMode="numeric" pattern="[0-9]*"
                      style={{ ...INP, height: 58, fontSize: '1.15rem', letterSpacing: '0.1em', textAlign: 'center', marginBottom: 16 }}
                      placeholder="Enter 10-digit number"
                      value={coPhone}
                      onChange={e => setCoPhone(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                      onKeyDown={e => { if (e.key === 'Enter') searchMyVisit(); }}
                      maxLength={10} autoFocus
                    />
                  </>
                ) : (
                  <>
                    <label style={{ ...LBL, fontSize: '1rem' }}>Visitor Name</label>
                    <input
                      type="text"
                      style={{ ...INP, height: 58, fontSize: '1.1rem', textAlign: 'center', marginBottom: 16 }}
                      placeholder="Enter visitor's name"
                      value={coName}
                      onChange={e => setCoName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') searchMyVisit(); }}
                      autoFocus
                    />
                  </>
                )}

                <button onClick={searchMyVisit} disabled={coLoading}
                  style={{ width: '100%', height: 56, background: '#F2652C', color: '#fff', border: 'none', borderRadius: 12, fontSize: '1.05rem', fontWeight: 700, cursor: 'pointer' }}>
                  {coLoading
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Searching…</>
                    : <><i className="bi bi-search me-2"></i>Find Visit</>}
                </button>
              </div>
            )}

            {/* ── Result state ── */}
            {!coDone && coSearched && (
              <div style={{ background: '#fff', borderRadius: 20, padding: 36, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>

                {/* Confirm single visit */}
                {coVisit ? (
                  <>
                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                      <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#e8f5ee', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                        <i className="bi bi-person-check-fill" style={{ color: '#086F3C', fontSize: '1.8rem' }}></i>
                      </div>
                      <h5 style={{ fontWeight: 800, color: '#1a1a2e', fontSize: '1.2rem', marginBottom: 4 }}>
                        {coSearchType === 'phone' ? 'Is this you?' : 'Confirm Checkout'}
                      </h5>
                      <p style={{ color: '#6c757d', margin: 0, fontSize: '0.92rem' }}>Confirm details before checking out</p>
                    </div>
                    <div style={{ background: '#f8fafb', borderRadius: 14, padding: 20, marginBottom: 24, border: '1.5px solid #e9ecef' }}>
                      {[
                        { icon: 'bi-person-fill',  label: 'Name',    value: coVisit.visitor?.name },
                        { icon: 'bi-phone-fill',   label: 'Mobile',  value: coVisit.visitor?.phone },
                        { icon: 'bi-building',     label: 'Company', value: coVisit.visitor?.company || '—' },
                        { icon: 'bi-person-check', label: 'Meeting', value: coVisit.toWhomMeet },
                        { icon: 'bi-clock-fill',   label: 'Time In', value: new Date(coVisit.timeIn).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) },
                        { icon: 'bi-tag-fill',     label: 'Purpose', value: coVisit.purposeDetails || coVisit.purpose },
                      ].map(({ icon, label, value }) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #e9ecef' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6c757d', fontSize: '0.92rem' }}>
                            <i className={`bi ${icon}`} style={{ color: '#086F3C', width: 16 }}></i>{label}
                          </span>
                          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a2e' }}>{value}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <button onClick={() => { coSearchType === 'name' && coVisits.length > 1 ? setCoVisit(null) : resetCheckout(); }}
                        style={{ flex: 1, height: 52, background: '#fff', color: '#6c757d', border: '1.5px solid #dee2e6', borderRadius: 12, fontSize: '1rem', fontWeight: 600, cursor: 'pointer' }}>
                        {coSearchType === 'name' && coVisits.length > 1 ? '← Back' : 'Not Me'}
                      </button>
                      <button onClick={confirmCheckout} disabled={coCheckingOut}
                        style={{ flex: 2, height: 52, background: '#086F3C', color: '#fff', border: 'none', borderRadius: 12, fontSize: '1.05rem', fontWeight: 700, cursor: 'pointer' }}>
                        {coCheckingOut
                          ? <><span className="spinner-border spinner-border-sm me-2"></span>Checking out…</>
                          : <><i className="bi bi-check2-circle me-2"></i>Confirm Checkout</>}
                      </button>
                    </div>
                  </>

                ) : coSearchType === 'name' && coVisits.length > 1 ? (
                  /* Name search — multiple results list */
                  <>
                    <div style={{ marginBottom: 20 }}>
                      <h5 style={{ fontWeight: 800, color: '#1a1a2e', marginBottom: 4 }}>
                        {coVisits.length} visitors found
                      </h5>
                      <p style={{ color: '#6c757d', fontSize: '0.92rem', margin: 0 }}>Select the correct visitor to checkout</p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                      {coVisits.map(v => (
                        <button key={v._id} onClick={() => setCoVisit(v)}
                          style={{ background: '#f8fafb', border: '1.5px solid #e9ecef', borderRadius: 14, padding: '14px 16px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = '#086F3C'}
                          onMouseLeave={e => e.currentTarget.style.borderColor = '#e9ecef'}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e' }}>{v.visitor?.name}</div>
                              <div style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: 2 }}>
                                <i className="bi bi-phone-fill me-1"></i>{v.visitor?.phone}
                                {v.visitor?.company && <><span style={{ margin: '0 6px' }}>·</span><i className="bi bi-building me-1"></i>{v.visitor?.company}</>}
                              </div>
                              <div style={{ fontSize: '0.82rem', color: '#6c757d', marginTop: 2 }}>
                                <i className="bi bi-person-check me-1"></i>Meeting: {v.toWhomMeet}
                                <span style={{ margin: '0 6px' }}>·</span>
                                <i className="bi bi-clock me-1"></i>{new Date(v.timeIn).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                              </div>
                            </div>
                            <i className="bi bi-chevron-right" style={{ color: '#086F3C', fontSize: '1.2rem', flexShrink: 0 }}></i>
                          </div>
                        </button>
                      ))}
                    </div>
                    <button onClick={resetCheckout}
                      style={{ width: '100%', height: 48, background: '#f0f4f8', color: '#374151', border: '1.5px solid #dee2e6', borderRadius: 12, fontSize: '1rem', fontWeight: 600, cursor: 'pointer' }}>
                      <i className="bi bi-arrow-left me-2"></i>Search Again
                    </button>
                  </>

                ) : (
                  /* No results */
                  <>
                    <div style={{ textAlign: 'center', padding: '20px 0 28px' }}>
                      <div style={{ width: 70, height: 70, borderRadius: '50%', background: '#fff3ee', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                        <i className="bi bi-person-x" style={{ color: '#F2652C', fontSize: '2rem' }}></i>
                      </div>
                      <h5 style={{ fontWeight: 800, color: '#1a1a2e', marginBottom: 8 }}>No Active Visit Found</h5>
                      <p style={{ color: '#6c757d', fontSize: '0.95rem' }}>
                        No active entry found for <strong>{coSearchType === 'phone' ? coPhone : coName}</strong>.<br/>
                        Please ask reception staff for assistance.
                      </p>
                    </div>
                    <button onClick={resetCheckout}
                      style={{ width: '100%', height: 52, background: '#f0f4f8', color: '#374151', border: '1.5px solid #dee2e6', borderRadius: 12, fontSize: '1rem', fontWeight: 600, cursor: 'pointer' }}>
                      <i className="bi bi-arrow-left me-2"></i>Try Again
                    </button>
                  </>
                )}
              </div>
            )}

          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════
          REGISTER MODE
      ════════════════════════════════════════════════ */}
      {mode === 'register' && (
        <form onSubmit={handleSubmit} noValidate style={{ padding: '20px' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div className="row g-3">

              {/* ══ LEFT — Form ══ */}
              <div className="col-12 col-lg-7">

                {/* Duplicate warning */}
                {activeVisitId && (
                  <div style={{ background: '#fff3cd', border: '1.5px solid #ffc107', borderRadius: 14, padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
                    <i className="bi bi-exclamation-triangle-fill" style={{ color: '#856404', fontSize: '1.4rem', flexShrink: 0 }}></i>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: '#856404', fontSize: '1rem' }}>Visitor Already Inside</div>
                      <div style={{ color: '#856404', fontSize: '0.88rem' }}>This visitor has an active entry today. Please checkout first before registering again.</div>
                    </div>
                    <button type="button" onClick={() => {
                        const phone = form.visitorPhone;
                        handleReset();
                        resetCheckout();
                        setCoPhone(phone);
                        setMode('checkout');
                      }}
                      style={{ background: '#F2652C', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 16px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      Go to Checkout →
                    </button>
                  </div>
                )}

                {/* ── CARD 1: Visitor Info ── */}
                <div style={{ background: '#fff', borderRadius: 16, padding: 24, marginBottom: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                  <SectionHeader icon="bi-person-vcard-fill" label="Visitor Information" color="#F2652C" bg="#fff3ee" />
                  <div className="row g-3">

                    {isGlobalRole && (
                      <div className="col-12">
                        <label style={LBL}>Office <span style={{ color: '#ef4444' }}>*</span></label>
                        <select style={{ ...INP, paddingLeft: 14 }}
                          value={selectedOfficeId}
                          onChange={e => { setSelectedOfficeId(e.target.value); setErrors(er => ({ ...er, officeId: '' })); }}>
                          <option value="">— Select office —</option>
                          {offices.map(o => <option key={o._id} value={o._id}>{o.name} ({o.code}) — {o.city}</option>)}
                        </select>
                        {errors.officeId && <div style={ERR}>{errors.officeId}</div>}
                      </div>
                    )}

                    {/* Mobile */}
                    <div className="col-12 col-sm-4">
                      <label style={LBL}>Mobile Number <span style={{ color: '#ef4444' }}>*</span></label>
                      <div style={{ display: 'flex' }}>
                        <span style={{ height: 54, display: 'flex', alignItems: 'center', padding: '0 14px', background: '#f8f9fa', border: '1.5px solid #dee2e6', borderRight: 'none', borderRadius: '12px 0 0 12px', color: '#6c757d', fontSize: '1.1rem', flexShrink: 0 }}>
                          <i className="bi bi-phone-fill"></i>
                        </span>
                        <input type="tel" name="visitorPhone" inputMode="numeric" pattern="[0-9]*"
                          style={{ ...INP, borderRadius: '0 12px 12px 0', borderLeft: 'none', borderColor: errors.visitorPhone ? '#ef4444' : '#dee2e6' }}
                          placeholder="10-digit number"
                          value={form.visitorPhone} onChange={handleChange} onBlur={handleBlur}
                          onKeyPress={e => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                          maxLength={10}
                        />
                      </div>
                      {errors.visitorPhone && <div style={ERR}>{errors.visitorPhone}</div>}
                      {foundVisitor && !errors.visitorPhone && (
                        <div style={{ background: '#e8f5ee', border: '1px solid #b7dfc9', borderRadius: 8, padding: '5px 12px', marginTop: 5, fontSize: '0.85rem', color: '#086F3C', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <i className="bi bi-person-check-fill"></i><strong>{foundVisitor.name}</strong> — auto-filled
                        </div>
                      )}
                    </div>

                    {/* Name */}
                    <div className="col-12 col-sm-4">
                      <label style={LBL}>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="text" name="visitorName"
                        style={{ ...INP, borderColor: errors.visitorName ? '#ef4444' : '#dee2e6' }}
                        placeholder="Visitor's full name"
                        value={form.visitorName} onChange={handleChange} onBlur={handleBlur}
                        onKeyPress={e => { if (!/[a-zA-Z\s]/.test(e.key)) e.preventDefault(); }}
                      />
                      {errors.visitorName && <div style={ERR}>{errors.visitorName}</div>}
                    </div>

                    {/* Company — mandatory */}
                    <div className="col-12 col-sm-4">
                      <label style={LBL}>Company / Organization <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="text" name="visitorCompany"
                        style={{ ...INP, borderColor: errors.visitorCompany ? '#ef4444' : '#dee2e6' }}
                        placeholder="Company name"
                        value={form.visitorCompany} onChange={handleChange} onBlur={handleBlur}
                      />
                      {errors.visitorCompany && <div style={ERR}>{errors.visitorCompany}</div>}
                    </div>

                    {/* Address */}
                    <div className="col-12">
                      <label style={LBL}>Address <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="text" name="visitorAddress"
                        style={{ ...INP, borderColor: errors.visitorAddress ? '#ef4444' : '#dee2e6' }}
                        placeholder="Residential or office address"
                        value={form.visitorAddress} onChange={handleChange} onBlur={handleBlur}
                      />
                      {errors.visitorAddress && <div style={ERR}>{errors.visitorAddress}</div>}
                    </div>

                  </div>
                </div>

                {/* ── CARD 2: Visit Details ── */}
                <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                  <SectionHeader icon="bi-briefcase-fill" label="Visit Details" color="#0078d4" bg="#eef6ff" />
                  <div className="row g-3">

                    {/* Meeting With */}
                    <div className="col-12 col-sm-4">
                      <label style={LBL}>Meeting With <span style={{ color: '#ef4444' }}>*</span></label>
                      <div style={{ position: 'relative' }}>
                        <input type="text" name="toWhomMeet"
                          style={{ ...INP, borderColor: errors.toWhomMeet ? '#ef4444' : '#dee2e6' }}
                          placeholder="Search employee name..."
                          value={form.toWhomMeet} onChange={handleChange}
                          onFocus={() => { empFocusedRef.current = true; if (empSuggestions.length > 0) setShowSuggestions(true); }}
                          onBlur={() => { empFocusedRef.current = false; handleBlur({ target: { name: 'toWhomMeet', value: form.toWhomMeet } }); setTimeout(() => setShowSuggestions(false), 250); }}
                          autoComplete="off"
                        />
                        {showSuggestions && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #e9ecef', borderRadius: 14, boxShadow: '0 8px 28px rgba(0,0,0,0.12)', zIndex: 1050, maxHeight: 280, overflowY: 'auto', marginTop: 4 }}>
                            {empSearching && (
                              <div style={{ padding: '14px 18px', textAlign: 'center', color: '#6c757d', fontSize: '0.95rem' }}>
                                <span className="spinner-border spinner-border-sm me-2" style={{ color: '#086F3C' }}></span>Searching…
                              </div>
                            )}
                            {!empSearching && empSuggestions.map(emp => (
                              <div key={emp.id || emp._id || emp.email}
                                style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', borderBottom: '1px solid #f0f0f0', minHeight: 60 }}
                                onMouseDown={() => selectEmployee(emp)}
                                onMouseEnter={e => e.currentTarget.style.background = '#fdf0eb'}
                                onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#F2652C', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                                  {emp.name?.[0]?.toUpperCase()}
                                </div>
                                <div style={{ overflow: 'hidden', flex: 1 }}>
                                  <div style={{ fontWeight: 700, fontSize: '0.98rem' }}>{emp.name}</div>
                                  <div style={{ fontSize: '0.78rem', color: '#6c757d' }}>{emp.department}{emp.jobTitle && emp.department ? ' · ' : ''}{emp.jobTitle}</div>
                                </div>
                                {emp.email && <span style={{ background: '#e8f5ee', color: '#086F3C', fontSize: '0.72rem', fontWeight: 600, padding: '3px 10px', borderRadius: 20, flexShrink: 0 }}>Notify</span>}
                              </div>
                            ))}
                            {!empSearching && empSuggestions.length === 0 && (
                              <div style={{ padding: '14px 18px', textAlign: 'center', color: '#6c757d', fontSize: '0.95rem' }}>
                                <i className="bi bi-person-x me-2"></i>No employees found
                              </div>
                            )}
                          </div>
                        )}
                        {errors.toWhomMeet && <div style={ERR}>{errors.toWhomMeet}</div>}
                      </div>
                    </div>

                    {/* Department */}
                    <div className="col-12 col-sm-4">
                      <label style={LBL}>
                        Department
                        {hostEmail && form.department && <span style={{ marginLeft: 8, background: '#e8f0fb', color: '#0078d4', fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 20, verticalAlign: 'middle' }}>Auto</span>}
                      </label>
                      <input type="text" name="department" style={INP}
                        placeholder="Auto-filled from directory"
                        value={form.department} onChange={handleChange} />
                    </div>

                    {/* Purpose */}
                    <div className="col-12 col-sm-4">
                      <label style={LBL}>Purpose <span style={{ color: '#ef4444' }}>*</span></label>
                      <select name="purposeType" style={{ ...INP, paddingLeft: 14 }} value={form.purposeType} onChange={handleChange}>
                        {PURPOSE_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>

                    {form.purposeType === 'Other' && (
                      <div className="col-12 col-sm-6">
                        <label style={LBL}>Specify Purpose <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="text" name="purposeOther"
                          style={{ ...INP, borderColor: errors.purposeOther ? '#ef4444' : '#dee2e6' }}
                          placeholder="Describe the purpose"
                          value={form.purposeOther} onChange={handleChange}
                          onBlur={e => setErrors(er => ({ ...er, purposeOther: e.target.value.trim() ? '' : 'Required.' }))} />
                        {errors.purposeOther && <div style={ERR}>{errors.purposeOther}</div>}
                      </div>
                    )}

                    {form.purposeType === 'Vendor Visit' && (
                      <div className="col-12 col-sm-6">
                        <label style={LBL}>Vendor Company <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="text" name="vendorCompany"
                          style={{ ...INP, borderColor: errors.vendorCompany ? '#ef4444' : '#dee2e6' }}
                          placeholder="Vendor company name"
                          value={form.vendorCompany} onChange={handleChange}
                          onBlur={e => setErrors(er => ({ ...er, vendorCompany: e.target.value.trim() ? '' : 'Required.' }))} />
                        {errors.vendorCompany && <div style={ERR}>{errors.vendorCompany}</div>}
                      </div>
                    )}

                    {/* Total Visitors in Group */}
                    <div className="col-12 col-sm-6">
                      <label style={LBL}>Total People Arriving <span style={{ color: '#ef4444' }}>*</span></label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="number" name="totalVisitors" min="1" max="10"
                          style={{ ...INP, flex: 1 }}
                          placeholder="1"
                          value={form.totalVisitors} onChange={handleChange} />
                        <span style={{ color: '#6c757d', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                          {form.totalVisitors > 1 ? `(You +${parseInt(form.totalVisitors) - 1})` : '(Just you)'}
                        </span>
                      </div>
                    </div>

                    {/* Remarks */}
                    <div className="col-12 col-sm-6">
                      <label style={LBL}>Remarks</label>
                      <input type="text" name="remarks" style={INP}
                        placeholder="Any additional notes (optional)"
                        value={form.remarks} onChange={handleChange} />
                    </div>

                  </div>
                </div>
              </div>
              {/* ══ END LEFT ══ */}

              {/* ══ RIGHT — Actions + Preview ══ */}
              <div className="col-12 col-lg-5">
                <div style={{ position: 'sticky', top: 16 }}>

                  {/* Submit */}
                  <button type="submit"
                    style={{ width: '100%', height: 60, background: !!activeVisitId ? '#9ca3af' : 'linear-gradient(135deg,#086F3C,#0a8f4e)', color: '#fff', border: 'none', borderRadius: 16, fontSize: '1.1rem', fontWeight: 800, cursor: !!activeVisitId ? 'not-allowed' : 'pointer', marginBottom: 12, boxShadow: !!activeVisitId ? 'none' : '0 6px 20px rgba(8,111,60,0.3)', letterSpacing: '0.02em', transition: 'all 0.15s' }}
                    disabled={submitting || !!activeVisitId}>
                    {submitting
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>Registering…</>
                      : <><i className="bi bi-check2-circle me-2" style={{ fontSize: '1.2rem' }}></i>Record Visitor Entry</>}
                  </button>

                  <button type="button" onClick={handleReset}
                    style={{ width: '100%', height: 50, background: '#fff', color: '#6c757d', border: '1.5px solid #dee2e6', borderRadius: 14, fontSize: '1rem', fontWeight: 600, cursor: 'pointer', marginBottom: 16 }}>
                    <i className="bi bi-arrow-counterclockwise me-2"></i>Reset Form
                  </button>

                  {/* Entry Preview */}
                  <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                    <div style={{ background: 'linear-gradient(135deg,#086F3C,#0a8f4e)', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Entry Preview</span>
                      <span style={{ background: 'rgba(255,255,255,0.22)', color: '#fff', fontSize: '0.72rem', fontWeight: 600, padding: '3px 12px', borderRadius: 20 }}>
                        {foundVisitor ? '↩ Returning Visitor' : 'New Visitor'}
                      </span>
                    </div>
                    <div style={{ padding: '6px 20px 12px', maxHeight: 300, overflowY: 'auto' }}>
                      {[
                        ...(isGlobalRole ? [{ icon: 'bi-building',     label: 'Office',   value: offices.find(o => o._id === selectedOfficeId)?.name || '—' }] : []),
                        { icon: 'bi-person-fill',    label: 'Name',    value: form.visitorName    || '—' },
                        { icon: 'bi-phone-fill',     label: 'Mobile',  value: form.visitorPhone   || '—' },
                        { icon: 'bi-people-fill',    label: 'Group',   value: form.totalVisitors > 1 ? `${form.totalVisitors} people` : '1 person' },
                        { icon: 'bi-briefcase-fill', label: 'Company', value: form.visitorCompany || '—' },
                        { icon: 'bi-person-check',   label: 'Meeting', value: form.toWhomMeet     || '—' },
                        { icon: 'bi-diagram-3-fill', label: 'Dept',    value: form.department     || '—' },
                        { icon: 'bi-tag-fill',       label: 'Purpose', value: form.purposeType === 'Other' ? (form.purposeOther || 'Other') : form.purposeType },
                        { icon: 'bi-clock-fill',     label: 'Time In', value: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) },
                      ].map(({ icon, label, value }) => (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f2f2f2' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6c757d', fontSize: '0.82rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            <i className={`bi ${icon}`} style={{ color: '#086F3C', width: 16 }}></i>{label}
                          </span>
                          <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#231F20', maxWidth: '55%', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </div>
              {/* ══ END RIGHT ══ */}

            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default VisitorRegistration;
